import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { ROLE_NAMES, SORT_MAPPINGS } from "shared/constants";
import { uploadFileWeb } from "shared/helpers/shared.helpers";
import PersonService from "shared/services/Person.service";
import { mapObjectArrayByKey } from "shared/utils/arrayOfObjects.utils";
import { mergeReplace } from "shared/utils/payload.utils";
import CustomModal from "web/components/form-elements/CustomModal.component";
import CustomButton from "web/components/form-elements/inputs/CustomButton.component";
import { SCREEN_DASHBOARD_MANAGE_PERSON } from "web/constants";
import { actionTypes } from "web/reducers/organization.reducer";
import PaginatableTable from "webComponentsFormElements/PaginatableTable.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import { AuthContext } from "webContext/Auth.context";
import { ConfirmationContext } from "webContext/Confirmation.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";
import CreatePersonForm from "../user-management/CreatePersonForm.component";
import ImportUsersForm from "../user-management/ImportUsersForm.component";

const classes = StyleSheet.create({
  root: {
    flex: 1,
    paddingHorizontal: 100,
    gap: 20,
    minWidth: 900,
  },
  link: {
    color: theme.color.link,
    fontWeight: theme.font.weight.medium,
  },
});

export default function PersonListScreen({ navigation }) {
  const { personList, getPersons, dispatch } = useContext(OrganizationContext);
  const { showToast } = useContext(ToastContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const { isMessagingManager } = useContext(AuthContext);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortOrder, setSortOrder] = useState("2"); // Default to Last Name A-Z (matches our options)
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [canSave, setCanSave] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateEmailList, setDuplicateEmailList] = useState("");
  const [isCreatePersonModalOpen, setIsCreatePersonModalOpen] = useState(false);
  const [minimalNewUserData, setMinimalNewUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleId: null,
    file: null,
    signedFileUrl: null,
  });
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const peopleByPersonId = useMemo(
    () => mapObjectArrayByKey(personList, "personId"),
    [personList],
  );

  useEffect(() => {
    getPersons(showDeleted);
  }, [getPersons, showDeleted]);

  const openCreatePersonModal = () => {
    setIsCreatePersonModalOpen(true);
  };

  const handleResetPasswordOrResendCode = async (person) => {
    const personFromList = peopleByPersonId[person.id];
    if (!personFromList) {
      showToast("Person not found. Please try again.", "error");
      return;
    }
    showConfirmation({
      prompt: `Are you sure you want to ${personFromList.status === "Unregistered" ? "resend the person code" : "reset the password"} for ${personFromList.firstName} ${personFromList.lastName}?`,
      onConfirm: async () => {
        try {
          if (personFromList.status === "Unregistered") {
            await PersonService.sendPersonCode({
              personId: personFromList.personId,
            });
            showToast(
              `Person code resent to ${personFromList.email}`,
              "success",
            );
          } else {
            await PersonService.resetPersonPassword(personFromList.personId);
            showToast(
              `Password reset email sent to ${personFromList.email}`,
              "success",
            );
          }
        } catch (error) {
          showToast("Failed to reset password or resend person code", "error");
        }
      },
    });
  };

  const handleToggleStatus = async (person) => {
    const personFromList = peopleByPersonId[person.id];
    if (!personFromList) {
      showToast("Person not found. Please try again.", "error");
      return;
    }
    const isActive = personFromList.status === "Active";
    const action = isActive ? "deactivate" : "activate";

    showConfirmation({
      prompt: `Are you sure you want to ${action} ${personFromList.firstName} ${personFromList.lastName}?`,
      onConfirm: async () => {
        try {
          await PersonService.togglePersonStatus(personFromList.personId);
          showToast(
            `Person ${personFromList.firstName} ${personFromList.lastName} successfully ${action}d`,
            "success",
          );
          getPersons(showDeleted);
        } catch (error) {
          showToast(`Failed to ${action} person`, "error");
        }
      },
    });
  };

  const handleDeletePerson = async (person) => {
    const personFromList = peopleByPersonId[person.id];
    if (!personFromList) {
      showToast("Person not found. Please try again.", "error");
      return;
    }

    showConfirmation({
      prompt: `Are you sure you want to permanently delete ${personFromList.firstName} ${personFromList.lastName}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await PersonService.deletePerson(personFromList.personId);
          showToast(
            `Person ${personFromList.firstName} ${personFromList.lastName} successfully deleted`,
            "success",
          );
          getPersons(showDeleted);
        } catch (error) {
          showToast("Failed to delete person", "error");
        }
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return theme.color.green;
      case "Deactivated":
        return theme.color.red;
      case "Unregistered":
        return theme.color.yellow;
      default:
        return theme.color.darkGrey;
    }
  };

  const renderTeamsList = (teams) => {
    if (!teams || teams.length === 0) {
      return "None";
    }
    return teams.length === 1 ? (
      teams[0].name
    ) : (
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {teams.map((team, index) => (
          <li key={index}>{team.name}</li>
        ))}
      </ul>
    );
  };

  const renderLocationsList = (locations) => {
    if (!locations || locations.length === 0) {
      return "None";
    }
    return locations.length === 1 ? (
      locations[0].name
    ) : (
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {locations.map((location, index) => (
          <li key={index}>{location.name}</li>
        ))}
      </ul>
    );
  };

  const columns = [
    {
      key: "name",
      title: "Name",
      flex: 1.5,
      alignment: "left",
    },
    {
      key: "title",
      title: "Title",
      flex: 1.5,
      alignment: "left",
    },
    {
      key: "location",
      title: "Places",
      flex: 1.2,
      alignment: "left",
    },
    {
      key: "teams",
      title: "Teams",
      flex: 1.8,
      alignment: "left",
    },
    {
      key: "access",
      title: "Access",
      flex: 1,
      alignment: "left",
    },
    {
      key: "status",
      title: "Status",
      flex: 1,
    },
    {
      key: "actions",
      title: "Actions",
      flex: 2.5,
      buttons: [
        {
          text: (row) => {
            if (row.status.value === "Deleted") {
              return null;
            }
            if (row.status.value === "Unregistered") {
              return "Resend Code";
            }
            return "Reset Password";
          },
          onPress: handleResetPasswordOrResendCode,
        },
        {
          text: (row) => {
            if (
              isMessagingManager ||
              row.status.value === "Unregistered" ||
              row.status.value === "Deleted"
            ) {
              return null;
            }
            return row.status.value === "Active" ? "Deactivate" : "Activate";
          },
          onPress: handleToggleStatus,
          variant: (row) => {
            return row.status.value === "Active" ? "danger" : "dark";
          },
        },
        {
          text: (row) => {
            if (
              row.status.value === "Unregistered" ||
              row.status.value === "Deactivated"
            ) {
              return "Delete";
            }
            return null;
          },
          onPress: handleDeletePerson,
          variant: "danger",
        },
      ],
    },
  ];

  const personSortOptions = [
    { label: "First Name (A-Z)", value: "0" },
    { label: "First Name (Z-A)", value: "1" },
    { label: "Last Name (A-Z)", value: "2" },
    { label: "Last Name (Z-A)", value: "3" },
    { label: "Position (A-Z)", value: "6" },
    { label: "Position (Z-A)", value: "7" },
    { label: "Status (A-Z)", value: "4" },
    { label: "Status (Z-A)", value: "5" },
  ];

  const tableData = useMemo(() => {
    if (!personList) {
      return [];
    }

    let filteredPersonList = [...personList];

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredPersonList = filteredPersonList.filter((person) => {
        const fullName = `${person.firstName} ${person.lastName}`.toLowerCase();
        const position = (person.position || "").toLowerCase();
        const location = (
          person._associations?.Locations?.[0]?.name || ""
        ).toLowerCase();
        const access = (ROLE_NAMES[person.roleId] || "").toLowerCase();

        return (
          fullName.includes(searchLower) ||
          position.includes(searchLower) ||
          location.includes(searchLower) ||
          access.includes(searchLower)
        );
      });
    }

    const sortedPersonList = [...filteredPersonList];

    // Apply sorting based on sortOrder
    if (sortOrder !== undefined) {
      const sortConfig = SORT_MAPPINGS[parseInt(sortOrder, 10)];
      if (sortConfig) {
        const { field, direction } = sortConfig;
        sortedPersonList.sort((a, b) => {
          let valA, valB;

          if (field === "firstName") {
            valA = a.firstName || "";
            valB = b.firstName || "";
          } else if (field === "lastName") {
            valA = a.lastName || "";
            valB = b.lastName || "";
          } else if (field === "position") {
            valA = a.position || "";
            valB = b.position || "";
          } else if (field === "status") {
            valA = a.status || "";
            valB = b.status || "";
          } else if (field === "roleId") {
            valA = ROLE_NAMES[a.roleId] || "";
            valB = ROLE_NAMES[b.roleId] || "";
          }

          if (direction === "asc") {
            return valA.localeCompare(valB);
          } else {
            return valB.localeCompare(valA);
          }
        });
      }
    }

    return sortedPersonList.map((person) => ({
      id: person.personId,
      name: (
        <PressableContainer
          style={classes.link}
          onPress={() =>
            navigation.navigate(SCREEN_DASHBOARD_MANAGE_PERSON, {
              personId: person.personId,
              personName: `${person.firstName} ${person.lastName}`,
            })
          }
        >
          <Text>
            {person.firstName} {person.lastName}
          </Text>
        </PressableContainer>
      ),
      title: person.position || "None",
      location: renderLocationsList(person._associations?.Locations),
      teams: renderTeamsList(person._associations?.Teams),
      access: ROLE_NAMES[person.roleId] || "Unknown",
      status: {
        color: getStatusColor(person.status),
        value: person.status,
      },
    }));
  }, [personList, sortOrder, searchTerm, navigation]);
  const handlePagination = ({
    direction,
    rowsPerPage,
    sortOrder: newSortOrder,
  }) => {
    if (
      direction === "next" &&
      pageNumber < Math.ceil(tableData.length / pageSize)
    ) {
      setPageNumber(pageNumber + 1);
    } else if (direction === "prev" && pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }

    if (rowsPerPage) {
      setPageSize(rowsPerPage);
      setPageNumber(1); // Reset to first page when changing page size
    }

    if (newSortOrder !== undefined) {
      setSortOrder(newSortOrder);
    }
  };

  const openImportModal = () => {
    setIsImportModalOpen(true);
  };

  const onModalClose = () => {
    setIsImportModalOpen(false);
    setIsCreatePersonModalOpen(false);
    setMinimalNewUserData({});
    setUsersData([]);
    setCanSave(false);
  };

  const handleImportUsers = async () => {
    try {
      const response = await PersonService.bulkInsert(usersData);
      if (response?.payload) {
        showToast(
          `Successfully imported ${response.payload.users.length} People, ${response.payload.teams.length} teams,
          and ${response.payload.locations.length} locations.`,
          "success",
        );
        await getPersons(); // Refresh the list
        setUsersData([]);
        setCanSave(false);
        setIsImportModalOpen(false);
      }
    } catch (error) {
      if (__DEV__) {
        console.error("Error importing users:", error);
      }

      const dupRows =
        error?.response?.status === 409 ? error?.response?.data?.payload : null;

      if (Array.isArray(dupRows)) {
        const pickEmail = (row) => row?.email ?? null;

        const emails = Array.from(
          new Set(
            dupRows
              .map(pickEmail)
              .filter(Boolean)
              .map((email) => String(email).trim().toLowerCase()),
          ),
        );

        const list = emails.join(", ");
        const msg = emails.length
          ? `The following emails already exist in the system. Please remove these rows and try again: ${list}`
          : "Some rows contain duplicate emails already in the system. Please remove those rows and try again.";
        setDuplicateEmailList(msg);
        setIsDuplicateModalOpen(true);
      } else {
        console.error("ERROR", error);
        showToast("Failed to import People.", "error");
      }
    }
  };

  const handleCreate = useCallback(async () => {
    try {
      const fileId = selectedImageFile
        ? (await uploadFileWeb(selectedImageFile)).fileId
        : previewImageUrl !== minimalNewUserData.signedFileUrl
          ? null
          : minimalNewUserData.file || null;

      const userPayload = { ...minimalNewUserData, fileId };
      delete userPayload.signedFileUrl;

      const person = await PersonService.upsertPerson(userPayload);
      const newList = mergeReplace(personList, person.payload, "personId");
      dispatch({ type: actionTypes.SET_PERSONS, payload: newList });
      setIsCreatePersonModalOpen(false);
      setCanSave(false);
      showToast("Successfully saved.", "success");

      // Navigate to ManagePersonScreen with the new person's ID
      navigation.navigate(SCREEN_DASHBOARD_MANAGE_PERSON, {
        personId: person.payload.personId,
      });
    } catch (error) {
      if (__DEV__) {
        console.error("Error saving person:", error);
      }
      if (error.response?.data?.message === "Email already in use.") {
        showToast(error.response.data.message, "error");
      } else {
        showToast("Failed to save person. Please try again.", "error");
      }
    }
  }, [
    minimalNewUserData,
    previewImageUrl,
    selectedImageFile,
    showToast,
    navigation,
    dispatch,
    personList,
  ]);

  return (
    <View style={classes.root}>
      <View style={{ flexDirection: "row", gap: 10, alignItems: "center" }}>
        <SearchBar
          placeholder="Search by name, title, location, or access level..."
          value={searchTerm}
          onChangeText={(value) => {
            setSearchTerm(value);
            setPageNumber(1);
          }}
          style={{
            margin: 0,
            minHeight: 50,
            maxHeight: 50,
            flex: 1,
          }}
        />
        <TouchableOpacity
          onPress={() => setShowDeleted((prev) => !prev)}
          style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
        >
          <Icon
            name={showDeleted ? "checkbox-marked" : "checkbox-blank-outline"}
            size={22}
            color={theme.color.darkGrey}
          />
          <Text>Show deleted</Text>
        </TouchableOpacity>
        <CustomButton
          onPress={openImportModal}
          title="Import People from CSV"
          icon="account-group-outline"
          variant="dark"
        />
        <CustomButton
          onPress={openCreatePersonModal}
          title="Add Person"
          icon="plus-circle-outline"
          variant="dark"
        />
      </View>
      <PaginatableTable
        columns={columns}
        data={tableData}
        sortOptions={personSortOptions || []}
        sortOrder={sortOrder}
        handlePagination={handlePagination}
        paginatable={false}
      />
      <CustomModal
        title="People CSV Import"
        open={isImportModalOpen}
        canSave={canSave}
        onClose={onModalClose}
        onSave={() => handleImportUsers()}
        saveTitle="Import People"
        scroll={true}
      >
        <ImportUsersForm
          setCanSave={setCanSave}
          setUsersData={setUsersData}
          usersData={usersData}
        />
      </CustomModal>
      <CustomModal
        title="Duplicate Emails Found"
        open={isDuplicateModalOpen}
        canSave={false}
        onClose={() => setIsDuplicateModalOpen(false)}
        scroll={true}
      >
        <Text
          style={{
            color: theme.color.darkGrey,
            whiteSpace: "pre-wrap",
            fontSize: theme.font.size.standard,
          }}
        >
          {duplicateEmailList}
        </Text>
      </CustomModal>
      <CustomModal
        title={"Create Person"}
        open={isCreatePersonModalOpen}
        canSave={canSave}
        onClose={onModalClose}
        onSave={() => handleCreate()}
      >
        <CreatePersonForm
          editUser={minimalNewUserData}
          setCanSave={setCanSave}
          setUserToUpsert={setMinimalNewUserData}
          previewImageUrl={previewImageUrl}
          setPreviewImageUrl={setPreviewImageUrl}
          selectedImageFile={selectedImageFile}
          setSelectedImageFile={setSelectedImageFile}
        />
      </CustomModal>
    </View>
  );
}
